// Quote request panel — Stream 3 PR-2.
//
// Right-rail card for custom-quote (pricingMode === 'quote')
// listings. Mirrors the "Standard kit → OrderPanel" pattern with a
// different inner form: date · guests · add-ons of interest · notes.
//
// The form composes a structured message body and submits it
// through the existing onSubmitInquiry path (per design doc §9 —
// the default-negotiation transaction state machine is out of
// scope; for now quote requests reach the host as a richly-
// formatted inquiry message).

import React, { useState } from 'react';
import classNames from 'classnames';

import {
  ADDON_TIER_LABELS,
  findAddonCategory,
} from '../../../data/addon-categories';
import { listingUnitLabel } from '../../../util/editListingHelpers';
import { priceWithFees } from '../../../util/listingHelpers';
import { extractZip } from '../../../util/usZip';
import { Button } from '../../../components';
import DeliveryRangeCheck from '../SectionFulfillmentOptions/DeliveryRangeCheck';

import css from './QuoteRequestPanel.module.css';

// Same vocabulary as the flat-rate booking panel; only the options the
// listing actually offers render.
const FULFILLMENT_OPTIONS = [
  { key: 'pickup', label: 'Pickup' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'setup', label: 'Setup & breakdown' },
];

const formatStartingAt = cents => {
  if (typeof cents !== 'number' || cents <= 0) return null;
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`;
};

const QuoteRequestPanel = props => {
  const {
    className,
    listing,
    authorDisplayName,
    onSubmitQuoteRequest,
    inProgress = false,
    error,
    isOwnListing = false,
  } = props;

  const publicData = listing?.attributes?.publicData || {};
  // Renter-facing "Starting at" — show the all-in floor (base + 15% fee),
  // never the bare base price. The search card and MobileBookingBar
  // already mark this same startingPrice up; desktop must match.
  const startingPrice = formatStartingAt(priceWithFees(publicData.startingPrice));
  const startingPriceUnit = listingUnitLabel(publicData);
  const addOns = Array.isArray(publicData.addOnCategories)
    ? publicData.addOnCategories
    : [];

  const [eventDate, setEventDate] = useState('');
  const [guests, setGuests] = useState('');
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [notes, setNotes] = useState('');
  // Fulfillment is asked UP FRONT, like flat-rate bookings (owner
  // decision 2026-07-02) — renters can change their mind later in the
  // thread, but the host should be able to price delivery/setup on the
  // first offer. Only the options this listing actually offers show.
  const [fulfillment, setFulfillment] = useState('');
  const [eventAddress, setEventAddress] = useState(null);

  const offeredFulfillment = Array.isArray(publicData.fulfillment)
    ? FULFILLMENT_OPTIONS.filter(o => publicData.fulfillment.includes(o.key))
    : [];
  const needsAddress = fulfillment === 'delivery' || fulfillment === 'setup';
  const geolocation = listing?.attributes?.geolocation;
  const listingZip = extractZip(publicData.location?.address);
  const activeRadius =
    fulfillment === 'setup'
      ? Number(publicData.setup_radius) || Number(publicData.delivery_radius) || null
      : Number(publicData.delivery_radius) || null;

  const toggleAddOn = key => {
    setSelectedAddOns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleSubmit = e => {
    e.preventDefault();
    // Compose a structured message body so the host gets a clean
    // inquiry. Each field is a labelled line — easy to scan in the
    // inbox; trivially parseable later when the negotiation state
    // machine lands.
    const fulfillmentLabel = FULFILLMENT_OPTIONS.find(o => o.key === fulfillment)?.label;
    const lines = [];
    lines.push(`Event date: ${eventDate || '(not specified)'}`);
    lines.push(`Guest count: ${guests || '(not specified)'}`);
    if (fulfillmentLabel) {
      lines.push(
        `Fulfillment: ${fulfillmentLabel}${eventAddress?.zip ? ` (ZIP ${eventAddress.zip})` : ''}`
      );
    }
    if (selectedAddOns.length > 0) {
      const labels = selectedAddOns
        .map(k => findAddonCategory(k)?.label || k)
        .join(', ');
      lines.push(`Add-ons of interest: ${labels}`);
    }
    if (notes.trim()) {
      lines.push('');
      lines.push(notes.trim());
    }
    const message = lines.join('\n');
    if (onSubmitQuoteRequest) {
      onSubmitQuoteRequest({
        message,
        eventDate,
        guests,
        selectedAddOns,
        fulfillment,
        eventZip: needsAddress ? eventAddress?.zip || null : null,
      });
    }
  };

  const missingFulfillment = offeredFulfillment.length > 0 && !fulfillment;
  const missingEventZip = needsAddress && !eventAddress?.zip;
  const submitDisabled =
    inProgress ||
    isOwnListing ||
    missingFulfillment ||
    missingEventZip ||
    (!eventDate && !guests && !notes.trim());
  const submitHint =
    inProgress || isOwnListing
      ? null
      : missingFulfillment
      ? 'Choose how you want to receive this kit to send your request.'
      : missingEventZip
      ? `Enter the event address so ${authorDisplayName || 'your host'} can quote ${
          fulfillment === 'setup' ? 'setup' : 'delivery'
        }.`
      : null;

  return (
    <aside className={classNames(css.root, className)}>
      <span className={css.badge}>Custom quote</span>

      <div className={css.priceRow}>
        <span className={css.pricePrefix}>Starting at</span>
        <span className={css.price}>{startingPrice || '—'}</span>
        {startingPrice && startingPriceUnit ? (
          <span className={css.pricePrefix}>{startingPriceUnit}</span>
        ) : null}
      </div>
      <p className={css.helper}>
        Final price depends on guest count, add-ons, and dates.
      </p>

      <form className={css.form} onSubmit={handleSubmit}>
        <div className={css.fieldRow}>
          <div className={css.field}>
            <label className={css.fieldLabel} htmlFor="quote-date">
              Event date
            </label>
            <input
              id="quote-date"
              type="date"
              className={css.fieldInput}
              value={eventDate}
              onChange={e => setEventDate(e.target.value)}
              disabled={isOwnListing}
            />
          </div>
          <div className={css.field}>
            <label className={css.fieldLabel} htmlFor="quote-guests">
              Guests
            </label>
            <input
              id="quote-guests"
              type="number"
              min="1"
              step="1"
              className={css.fieldInput}
              placeholder="16"
              value={guests}
              onChange={e => setGuests(e.target.value)}
              disabled={isOwnListing}
            />
          </div>
        </div>

        {offeredFulfillment.length > 0 && (
          <div className={css.field}>
            <label className={css.fieldLabel}>How do you want to receive this kit?</label>
            <div className={css.addOnChips}>
              {offeredFulfillment.map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  className={classNames(css.addOnChip, {
                    [css.addOnChipActive]: fulfillment === opt.key,
                  })}
                  onClick={() => {
                    setFulfillment(opt.key);
                    if (opt.key === 'pickup') setEventAddress(null);
                  }}
                  disabled={isOwnListing}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {needsAddress ? (
              <DeliveryRangeCheck
                listingGeolocation={geolocation}
                listingZip={listingZip}
                radiusMiles={activeRadius}
                onAddressChange={setEventAddress}
                addressLabel="Event address"
                helperText={`Where the kit is going, so ${authorDisplayName ||
                  'your host'} can quote ${fulfillment === 'setup' ? 'setup' : 'delivery'} accurately`}
              />
            ) : null}
          </div>
        )}

        {addOns.length > 0 && (
          <div className={css.field}>
            <label className={css.fieldLabel}>
              Add-ons you&rsquo;re interested in
            </label>
            <div className={css.addOnChips}>
              {addOns.map(row => {
                const cat = findAddonCategory(row?.category);
                if (!cat) return null;
                const active = selectedAddOns.includes(cat.key);
                return (
                  <button
                    key={cat.key}
                    type="button"
                    className={classNames(css.addOnChip, {
                      [css.addOnChipActive]: active,
                    })}
                    onClick={() => toggleAddOn(cat.key)}
                    disabled={isOwnListing}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className={css.field}>
          <label className={css.fieldLabel} htmlFor="quote-notes">
            Tell {authorDisplayName || 'your host'} about your event
          </label>
          <textarea
            id="quote-notes"
            className={classNames(css.fieldInput, css.textarea)}
            placeholder="Optional notes..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
            disabled={isOwnListing}
          />
        </div>

        {error && <p className={css.error}>Failed to send. Please try again.</p>}

        {submitDisabled && submitHint ? <p className={css.submitHint}>{submitHint}</p> : null}

        <Button
          type="submit"
          className={css.submit}
          inProgress={inProgress}
          disabled={submitDisabled}
        >
          Request a quote
        </Button>
        <p className={css.finePrint}>
          Free to request · No payment until you accept the quote
        </p>
      </form>
    </aside>
  );
};

export default QuoteRequestPanel;
